import { supabase } from '@/lib/supabase';
import { emailService } from './emailService';
import { getBizDevRepDetails } from './ticketService';
import { notificationService } from './notificationService';

export interface TicketNotification {
  id: string;
  ticket_id: string;
  user_id: string;
  type: string;
  message: string;
  read: boolean;
  created_at: string;
}

export interface CreateTicketNotificationInput {
  ticket_id: string;
  user_id: string;
  type: string;
  message: string;
  metadata?: any; // Added for potential future use or if notificationService expects it
}

const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatTicketNotificationEmail = async (notification: CreateTicketNotificationInput): Promise<{ text: string; html: string; subject: string }> => {


  // First fetch the latest comment
  const { data: latestComment, error: commentError } = await supabase
    .from('ticket_comments')
    .select(`
      content,
      created_at,
      is_internal,
      user_profiles!created_by (
        name,
        email
      )
    `)
    .eq('ticket_id', notification.ticket_id)
    .eq('is_internal', false)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();



  // Fetch all comments if this is a resolved ticket
  let allComments: { content: string; created_at: string; user_profiles: { name: string; email: string } }[] = [];
  if (notification.type === 'status_change') {
    const statusMatch = notification.message.match(/from (\w+) to (\w+)/);
    if (statusMatch && statusMatch[2] === 'resolved') {
      const { data: comments, error: commentsError } = await supabase
        .from('ticket_comments')
        .select(`
          content,
          created_at,
          is_internal,
          user_profiles!created_by (
            name,
            email
          )
        `)
        .eq('ticket_id', notification.ticket_id)
        .eq('is_internal', false)
        .order('created_at', { ascending: true });

      if (!commentsError && comments) {
        // Map user_profiles to the first element if it's an array
        allComments = comments.map((c: any) => ({
          ...c,
          user_profiles: Array.isArray(c.user_profiles) ? c.user_profiles[0] : c.user_profiles
        }));
      }
    }
  }

  if (commentError) {
    console.error('Error fetching latest comment:', commentError);
  }

  // Then fetch ticket details
  const ticketResult = await supabase
    .from('tickets')
    .select(`
      title,
      status,
      priority,
      description,
      created_by,
      assigned_to,
      project_id,
      user_profiles!created_by (
        name,
        email
      ),
      assigned_user:user_profiles!assigned_to (
        name,
        email
      )
    `)
    .eq('id', notification.ticket_id)
    .maybeSingle();

  const bizDevRepResult = await getBizDevRepDetails(ticketResult.data?.created_by || '');

  const ticket = ticketResult.data;
  const bizDevRep = bizDevRepResult?.user;

  if (ticketResult.error) {
    console.error('Error fetching ticket details:', ticketResult.error);
  }

  const ticketTitle = ticket?.title || 'Ticket';
  const ticketStatus = ticket?.status ? formatStatus(ticket.status) : 'Unknown Status';
  const ticketPriority = ticket?.priority ? formatStatus(ticket.priority) : 'Normal';
  const ticketDescription = ticket?.description || 'No description provided';
  const creatorName = Array.isArray(ticket?.user_profiles)
    ? (ticket?.user_profiles[0] && (ticket?.user_profiles[0] as { name?: string })?.name)
    : (ticket?.user_profiles && (ticket?.user_profiles as { name?: string })?.name) || 'Unknown';
  const assignedUserName = Array.isArray(ticket?.assigned_user)
    ? (ticket?.assigned_user[0] && (ticket?.assigned_user[0] as { name?: string })?.name)
    : (ticket?.assigned_user && (ticket?.assigned_user as { name?: string })?.name) || 'Unknown';
  const bizDevRepName = bizDevRep?.name || 'Not assigned';
  const commentContent = latestComment?.content || '';
  const commentAuthor = Array.isArray(latestComment?.user_profiles)
    ? (latestComment?.user_profiles[0] && (latestComment?.user_profiles[0] as { name?: string })?.name)
    : (latestComment?.user_profiles && (latestComment?.user_profiles as { name?: string })?.name) || 'Unknown';
  const commentDate = latestComment?.created_at ? new Date(latestComment.created_at).toLocaleString() : '';

  // Create direct links
  // Use environment variable if available (highest priority)
  let finalBaseUrl = 'https://go.solerro.com'; // fallback
  
  if (import.meta.env.VITE_APP_URL) {
    finalBaseUrl = import.meta.env.VITE_APP_URL;
  } else if (typeof window !== 'undefined' && window.location.origin) {
    // Only use localhost if we're actually in development and no env var is set
    if (import.meta.env.DEV && window.location.hostname === 'localhost') {
      finalBaseUrl = 'http://localhost:5174';
    } else {
      finalBaseUrl = window.location.origin;
    }
  }
  
  const ticketLink = `${finalBaseUrl}/app/support-tickets/${notification.ticket_id}`;



  // Create subject line based on notification type
  let subject = '';
  switch (notification.type) {
    case 'status_change':
      subject = `Ticket Status Updated: ${ticketTitle}`;
      break;
    case 'assignment_change':
      subject = `Ticket Assignment: ${ticketTitle}`;
      break;
    case 'comment':
      subject = `New Comment on Ticket: ${ticketTitle}`;
      break;
    case 'mention':
      subject = `You were mentioned in a ticket comment: ${ticketTitle}`;
      break;
    case 'ticket_created':
      subject = `New Ticket Created: ${ticketTitle}`;
      break;
    default:
      subject = `Ticket Notification: ${ticketTitle}`;
  }
  
  // Create message based on notification type
  let message = notification.message;
  let showTicketDetails = false;
  
  if (notification.type === 'ticket_created') {
    message = `Hi ${creatorName},\n\nThank you for reaching out! We've received your ticket and ${assignedUserName} will be taking care of it. They'll get back to you as soon as possible.\n\nHere are your ticket details:`;
    showTicketDetails = true;
  } else if (notification.type === 'comment') {
    message = `Hi ${creatorName},\n\nA new comment has been added to your ticket:\n\n${commentContent}\n\nYou can view all comments and ticket details in the support portal.`;
  } else if (notification.type === 'mention') {
    message = `Hi,\n\n${commentAuthor} mentioned you in a comment on ticket "${ticketTitle}":\n\n${commentContent}\n\nYou can view the full conversation and ticket details in the support portal.`;
  } else if (notification.type === 'status_change') {
    // Extract the old and new status from the message
    const statusMatch = message.match(/from (\w+) to (\w+)/);
    if (statusMatch) {
      const [_, /* oldStatus */, newStatus] = statusMatch;
      const formattedNewStatus = formatStatus(newStatus);
      
      if (newStatus === 'resolved') {
        message = `Hi ${creatorName},\n\nGreat news! Your ticket has been resolved. Thank you for your patience and for helping us improve our service. If you need anything else, please don't hesitate to reach out.\n\nHere's a summary of your ticket:`;
        showTicketDetails = true;
      } else if (newStatus === 'closed') {
        message = `Hi ${creatorName},\n\nYour ticket has been closed. We appreciate you working with us to resolve this matter. If you have any other questions or concerns, we're here to help!`;
      } else {
        message = `Hi ${creatorName},\n\nJust letting you know that your ticket's status has been updated to ${formattedNewStatus}. We're working on getting this resolved for you.`;
      }
    }
  } else if (notification.type === 'assignment_change') {
    message = `Hi ${creatorName},\n\nGreat news! ${assignedUserName} will be handling your ticket. They'll be in touch with you soon.`;
  }
  
  const text = `${subject}\n\n${message}${showTicketDetails ? `\n\nTicket Details:\nTitle: ${ticketTitle}\nStatus: ${ticketStatus}\nPriority: ${ticketPriority}\nDescription: ${ticketDescription}\nCreated By: ${creatorName}\nAssigned To: ${assignedUserName}\nBusiness Development Rep: ${bizDevRepName}` : ''}\n\nView Ticket: ${ticketLink}`;
  
  // Create HTML version for email
  let htmlContent = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
      <h2 style="color: #333; margin-bottom: 20px;">${subject}</h2>
      <div style="color: #666; line-height: 1.6; margin-bottom: 20px;">
        ${message.replace(/\n/g, '<br>')}
      </div>`;

  if (notification.type === 'comment' || notification.type === 'mention') {
    htmlContent += `
      <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; margin-bottom: 20px;">
        <h3 style="color: #333; margin-top: 0; margin-bottom: 10px;">Latest Comment</h3>
        <p style="margin: 0; color: #666;"><strong>From:</strong> ${commentAuthor}</p>
        <p style="margin: 0; color: #666;"><strong>Date:</strong> ${commentDate}</p>
        <div style="margin-top: 10px; padding: 10px; background-color: white; border-radius: 4px;">
          ${commentContent.replace(/\n/g, '<br>')}
        </div>
      </div>`;
  }

  if (showTicketDetails) {
    htmlContent += `
      <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; margin-bottom: 20px;">
        <h3 style="color: #333; margin-top: 0; margin-bottom: 10px;">Ticket Details</h3>
        <p style="margin: 0; color: #666;"><strong>Title:</strong> ${ticketTitle}</p>
        <p style="margin: 0; color: #666;"><strong>Status:</strong> ${ticketStatus}</p>
        <p style="margin: 0; color: #666;"><strong>Priority:</strong> ${ticketPriority}</p>
        <p style="margin: 0; color: #666;"><strong>Description:</strong></p>
        <div style="margin-top: 5px; padding: 10px; background-color: white; border-radius: 4px;">
          ${ticketDescription.replace(/\n/g, '<br>')}
        </div>
        <p style="margin: 10px 0 0 0; color: #666;"><strong>Created By:</strong> ${creatorName}</p>
        <p style="margin: 0; color: #666;"><strong>Assigned To:</strong> ${assignedUserName}</p>
        <p style="margin: 0; color: #666;"><strong>Business Development Rep:</strong> ${bizDevRepName}</p>
      </div>`;
  }

  // Add direct link section
  htmlContent += `
      <div style="margin-top: 20px; padding: 15px; background-color: #f8f9fa; border-radius: 4px;">
        <h3 style="color: #333; margin-top: 0; margin-bottom: 10px;">Quick Link</h3>
        <p style="margin: 0;">
          <a href="${ticketLink}" style="display: inline-block; padding: 8px 16px; background-color: #007bff; color: white; text-decoration: none; border-radius: 4px;">View Ticket</a>
        </p>
      </div>`;

  htmlContent += `
      <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e0e0e0; color: #999; font-size: 12px;">
        This is an automated notification from Solerro PM.
      </div>
    </div>`;



  return { text, html: htmlContent, subject };
};

export const ticketNotificationService = {
  async createNotification(input: CreateTicketNotificationInput): Promise<TicketNotification> {
    try {
      // Starting notification creation for ticket

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Fetch ticket details and latest comment for template variables
      const [ticketResult, commentResult, userResult] = await Promise.all([
        supabase
          .from('tickets')
          .select('title, status, priority, created_by, assigned_to')
          .eq('id', input.ticket_id)
          .single(),
        supabase
          .from('ticket_comments')
          .select('content, created_by, user_profiles!created_by(name)')
          .eq('ticket_id', input.ticket_id)
          .eq('is_internal', false)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('user_profiles')
          .select('name')
          .eq('id', user.id)
          .single()
      ]);

      const ticket = ticketResult.data;
      const latestComment = commentResult.data;
      const currentUser = userResult.data;

      // Fetch the ticket creator's name
      let creator = null;
      if (ticket?.created_by) {
        const { data: creatorData } = await supabase
          .from('user_profiles')
          .select('name')
          .eq('id', ticket.created_by)
          .maybeSingle();
        creator = creatorData;
      }

      // Prepare template variables
      const templateMetadata = {
        ticket_id: input.ticket_id,
        ticket_title: ticket?.title || 'Unknown ticket',
        sender_name: currentUser?.name || 'Someone',
        mentioned_by: currentUser?.name || 'Someone', // Alias for email templates
        created_by: creator?.name || 'Unknown',
        comment_text: latestComment?.content || input.message,
        priority: ticket?.priority || 'Normal',
        status: ticket?.status || 'Open',
        ...input.metadata
      };


      // Always use the main notification service for all user-facing notifications
      await notificationService.createNotification({
        user_id: input.user_id,
        type: input.type,
        title: 'Ticket Notification', // Template system will override
        message: input.message,
        metadata: templateMetadata,
        sender_id: user.id,
        sender_name: currentUser?.name || 'Someone'
      });

      // Trigger webhook in the background (don't wait for it)
      Promise.resolve().then(async () => {
        try {
          let commentContent = undefined;
          if (input.type === 'mention' || input.type === 'comment') {
            const { data: latestComment } = await supabase
              .from('ticket_comments')
              .select('content')
              .eq('ticket_id', input.ticket_id)
              .eq('is_internal', false)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            commentContent = latestComment?.content;
          }
          await import('./webhookService').then(({ webhookService }) =>
            webhookService.triggerWebhooks('notification_sent', {
              user_id: input.user_id,
              ticket_id: input.ticket_id,
              type: input.type,
              message: input.message,
              ...(commentContent ? { comment_content: commentContent } : {})
            })
          );
        } catch (webhookError) {
          console.error('[ticketNotificationService] Error triggering notification_sent webhook:', webhookError);
        }
      });

      return { id: 'placeholder', ticket_id: input.ticket_id, user_id: input.user_id, type: input.type, message: input.message, read: false, created_at: new Date().toISOString() };
    } catch (error) {
      console.error('[ticketNotificationService] Error creating notification:', {
        error,
        type: input.type,
        userId: input.user_id,
        ticketId: input.ticket_id
      });
      throw error;
    }
  },

  async getTicketNotifications(ticketId: string): Promise<TicketNotification[]> {
    const { data, error } = await supabase
      .from('ticket_notifications')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getUserNotifications(userId: string): Promise<TicketNotification[]> {
    const { data, error } = await supabase
      .from('ticket_notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async markAsRead(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from('ticket_notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) throw error;
  },

  async markAllAsRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from('ticket_notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) throw error;
  }
}; 